import {
  Briefcase,
  ReceiptText,
  Calculator,
  Package,
  FileText,
  ScrollText,
  ClipboardCheck,
  Truck,
  ScanText,
  Camera,
  Building2,
  Landmark,
  Award,
  Users,
  Handshake,
  Store,
  HardHat,
  ShoppingCart,
  GraduationCap,
  RefreshCw,
  Globe,
  Mail,
  FileCheck,
  Bike,
  ShoppingBag,
  Upload,
  Wand2,
  Download,
  CheckCircle2,
  Sparkles,
  Shield,
  Zap,
} from 'lucide-react';

const ICON = {
  briefcase: Briefcase,
  receipt: ReceiptText,
  calculator: Calculator,
  package: Package,
  file: FileText,
  scroll: ScrollText,
  clipboard: ClipboardCheck,
  truck: Truck,
  scan: ScanText,
  camera: Camera,
  building: Building2,
  landmark: Landmark,
  award: Award,
  users: Users,
  handshake: Handshake,
  store: Store,
  hardhat: HardHat,
  cart: ShoppingCart,
  graduate: GraduationCap,
  refresh: RefreshCw,
  globe: Globe,
  mail: Mail,
  fileCheck: FileCheck,
  bike: Bike,
  bag: ShoppingBag,
  upload: Upload,
  wand: Wand2,
  download: Download,
  check: CheckCircle2,
  sparkles: Sparkles,
  shield: Shield,
  zap: Zap,
};

export const TOOL_LANDING_SLUGS = [
  'business-proposal-builder',
  'invoice-generator',
  'quotation-generator',
  'purchase-order-builder',
  'resume-builder',
  'proforma-invoice',
  'delivery-note',
  'waybill-generator',
  'pdf-compression',
  'visa-passport-photo',
];

/** @typedef {{ label: string, icon: keyof typeof ICON }} PerfectForItem */
/** @typedef {{ title: string, description: string, icon?: keyof typeof ICON }} FeatureItem */
/** @typedef {{ step: number, title: string, description: string }} HowItWorksStep */
/** @typedef {{ question: string, answer: string }} FaqItem */
/** @typedef {{ slug: string, label: string }} RelatedTool */

/**
 * @param {string} slug
 * @param {string} label
 * @returns {RelatedTool}
 */
const related = (slug, label) => ({ slug, label });

const DEFAULT_HOW_IT_WORKS = [
  { step: 1, title: 'Choose a template', description: 'Pick a professional layout that matches your brand and document type.' },
  { step: 2, title: 'Fill in your details', description: 'Add company info, line items, and any smart-suggested content in minutes.' },
  { step: 3, title: 'Download your PDF', description: 'Export a polished, print-ready document you can send immediately.' },
];

const DEFAULT_FEATURES = [
  { title: 'Professional templates', description: 'Bank-ready layouts designed for business submissions and client-facing documents.', icon: 'file' },
  { title: 'Branded output', description: 'Upload your logo and company details for documents that look uniquely yours.', icon: 'sparkles' },
  { title: 'Fast generation', description: 'Create complete documents in minutes instead of hours of manual formatting.', icon: 'zap' },
  { title: 'PDF export', description: 'Download high-quality PDFs ready for email, printing, or official submissions.', icon: 'download' },
];

export const TOOL_LANDING_CONFIGS = {
  'business-proposal-builder': {
    slug: 'business-proposal-builder',
    pageLabel: 'BUSINESS PROPOSAL BUILDER',
    titleTag: 'Business Plans, Grant Proposals & Service Proposals | DocMania',
    metaDescription:
      'Generate business plans, grant proposals, and service proposals online. Smart SWOT, financials, and investor-ready PDFs in minutes.',
    canonicalPath: '/services/business-proposal-builder',
    heroHeadline: 'Win Funding. Close Clients. Generate Professional Business Proposals in Minutes.',
    heroSubtext:
      'Create polished business plans, grant proposals, and service proposals with built-in SWOT analysis, financials, and executive summaries.',
    keywords: [
      'business plan generator',
      'grant proposal generator',
      'business proposal template',
      'service proposal',
      'funding proposal',
      'bank loan business plan',
      'SME funding proposal',
      'investor-ready proposal',
    ],
    perfectFor: [
      { label: 'Business Funding', icon: 'landmark' },
      { label: 'Bank Loans', icon: 'building' },
      { label: 'Grant Applications', icon: 'award' },
      { label: 'SME Expansion', icon: 'store' },
      { label: 'Service Contracts', icon: 'handshake' },
      { label: 'Freelancers & Agencies', icon: 'users' },
    ],
    ctaLabel: 'Start Proposal Builder',
    ctaPath: '/services/business-proposal-builder',
    isProposalBuilder: true,
    loginGateMessage: 'Please sign in to generate proposals and download PDFs.',
    features: [
      { title: 'Smart Content Suggestions', description: 'Generate SWOT analysis, market research, financials, and executive summaries.', icon: 'wand' },
      { title: 'Multiple proposal types', description: 'Business plans, grant proposals, and service proposals in one workflow.', icon: 'briefcase' },
      { title: 'Investor-ready layouts', description: 'Templates structured for banks, grants, and client presentations.', icon: 'fileCheck' },
      { title: 'Cover pages & branding', description: 'Add your logo and company details for a polished first impression.', icon: 'sparkles' },
    ],
    howItWorks: [
      { step: 1, title: 'Pick your proposal type', description: 'Choose business plan, grant proposal, or service proposal to match your goal.' },
      { step: 2, title: 'Complete the guided wizard', description: 'Preview the flow, then sign in to unlock smart suggestions and PDF export.' },
      { step: 3, title: 'Download & submit', description: 'Export a professional PDF ready for investors, banks, or clients.' },
    ],
    faqs: [
      { question: 'What types of proposals can I create?', answer: 'DocMania supports business plans, grant proposals, and service proposals with tailored templates and smart content suggestions for each.' },
      { question: 'Can I preview before signing in?', answer: 'Yes. You can explore proposal categories and templates publicly. Sign in is required only when you generate content or download PDFs.' },
      { question: 'Are proposals suitable for bank loans?', answer: 'Yes. Templates include financial sections, SWOT analysis, and executive summaries structured for funding applications.' },
      { question: 'How long does it take?', answer: 'Most users complete a proposal in under 30 minutes with guided steps and auto-generated sections.' },
    ],
    relatedTools: [
      related('invoice-generator', 'Invoice Generator'),
      related('quotation-generator', 'Quotation Generator'),
      related('purchase-order-builder', 'Purchase Order Builder'),
      related('resume-builder', 'Resume Builder'),
    ],
  },

  'invoice-generator': {
    slug: 'invoice-generator',
    pageLabel: 'INVOICE GENERATOR',
    titleTag: 'Invoice Generator | Create Professional Invoices Online | DocMania',
    metaDescription:
      'Create professional invoices online with your logo and company details. Free invoice generator with itemized billing and PDF download.',
    canonicalPath: '/services/invoice-generator',
    heroHeadline: 'Create Professional Invoices in Minutes',
    heroSubtext: 'Create branded invoices with your logo, company details and itemized billing.',
    keywords: ['invoice generator', 'invoice maker', 'create invoice online', 'business invoice template', 'professional invoice'],
    perfectFor: [
      { label: 'Freelancers', icon: 'users' },
      { label: 'Small Businesses', icon: 'store' },
      { label: 'Vendors', icon: 'cart' },
      { label: 'Agencies', icon: 'briefcase' },
      { label: 'Consultants', icon: 'handshake' },
    ],
    ctaLabel: 'Create Invoice',
    ctaPath: '/services/invoice-generator',
    documentType: 'invoice',
    loginGateMessage: 'Please sign in to create invoices and download PDFs.',
    features: DEFAULT_FEATURES,
    howItWorks: DEFAULT_HOW_IT_WORKS,
    faqs: [
      { question: 'Can I add my company logo?', answer: 'Yes. Upload your logo and business details to brand every invoice you generate.' },
      { question: 'Is itemized billing supported?', answer: 'Yes. Add line items, quantities, rates, taxes, and totals automatically calculated.' },
      { question: 'What format is the output?', answer: 'Invoices export as high-quality PDFs ready to email or print.' },
    ],
    relatedTools: [
      related('quotation-generator', 'Quotation Generator'),
      related('proforma-invoice', 'Proforma Invoice'),
      related('purchase-order-builder', 'Purchase Order'),
      related('business-proposal-builder', 'Business Proposals'),
    ],
  },

  'quotation-generator': {
    slug: 'quotation-generator',
    pageLabel: 'QUOTATION GENERATOR',
    titleTag: 'Quotation Generator | Professional Price Quotes Online | DocMania',
    metaDescription:
      'Send professional quotations that win clients. Quotation generator with logo, pricing, validity periods, and branded PDF export.',
    canonicalPath: '/services/quotation-generator',
    heroHeadline: 'Send Professional Quotations That Win More Clients',
    heroSubtext: 'Create branded quotations with logo, pricing and company details instantly.',
    keywords: ['quotation generator', 'quotation template', 'quotation maker', 'price quotation', 'business quotation'],
    perfectFor: [
      { label: 'Service Businesses', icon: 'handshake' },
      { label: 'Contractors', icon: 'hardhat' },
      { label: 'Vendors', icon: 'cart' },
      { label: 'Agencies', icon: 'briefcase' },
    ],
    ctaLabel: 'Create Quotation',
    ctaPath: '/services/quotation-generator',
    documentType: 'quotation',
    loginGateMessage: 'Please sign in to create quotations and download PDFs.',
    features: DEFAULT_FEATURES,
    howItWorks: DEFAULT_HOW_IT_WORKS,
    faqs: [
      { question: 'Can quotations include validity periods?', answer: 'Yes. Add terms, validity dates, and detailed pricing tables in your quotation.' },
      { question: 'Will my quotation look professional?', answer: 'Templates are designed for client-facing business use with clean typography and branding.' },
      { question: 'How do I send the quotation?', answer: 'Download the PDF and email it directly to your client or print for in-person delivery.' },
    ],
    relatedTools: [
      related('invoice-generator', 'Invoice Generator'),
      related('proforma-invoice', 'Proforma Invoice'),
      related('purchase-order-builder', 'Purchase Order'),
      related('business-proposal-builder', 'Business Proposals'),
    ],
  },

  'purchase-order-builder': {
    slug: 'purchase-order-builder',
    pageLabel: 'PURCHASE ORDER BUILDER',
    titleTag: 'Purchase Order Generator | Create POs Online | DocMania',
    metaDescription:
      'Generate professional purchase orders for suppliers and vendors. PO templates with itemized lines, VAT, and PDF export.',
    canonicalPath: '/services/purchase-order-builder',
    heroHeadline: 'Generate Professional Purchase Orders in Minutes',
    heroSubtext: 'Create procurement-ready purchase orders for suppliers and vendors.',
    keywords: ['purchase order template', 'purchase order generator', 'PO generator', 'procurement document', 'vendor purchase order'],
    perfectFor: [
      { label: 'Procurement Teams', icon: 'cart' },
      { label: 'Businesses', icon: 'building' },
      { label: 'Vendors', icon: 'store' },
      { label: 'Suppliers', icon: 'package' },
    ],
    ctaLabel: 'Create Purchase Order',
    ctaPath: '/services/purchase-order-builder',
    documentType: 'purchase_order',
    loginGateMessage: 'Please sign in to create purchase orders and download PDFs.',
    features: [
      { title: 'Itemized line items', description: 'Add products, quantities, unit prices, and auto-calculated totals.', icon: 'clipboard' },
      { title: 'VAT & amount in words', description: 'Professional PO formatting with tax lines and written amounts.', icon: 'fileCheck' },
      { title: 'Supplier-ready layout', description: 'Clear vendor and delivery details for procurement workflows.', icon: 'truck' },
      { title: 'PDF export', description: 'Download print-ready purchase orders for approval and records.', icon: 'download' },
    ],
    howItWorks: DEFAULT_HOW_IT_WORKS,
    faqs: [
      { question: 'Are purchase orders suitable for vendor procurement?', answer: 'Yes. Templates include supplier details, delivery terms, and itemized product lines.' },
      { question: 'Can I reuse company details?', answer: 'Your profile stores company information for faster PO creation each time.' },
      { question: 'Is VAT supported?', answer: 'Yes. Add tax lines and see totals calculated automatically.' },
    ],
    relatedTools: [
      related('invoice-generator', 'Invoice Generator'),
      related('delivery-note', 'Delivery Note'),
      related('waybill-generator', 'Waybill Generator'),
      related('quotation-generator', 'Quotation Generator'),
    ],
  },

  'resume-builder': {
    slug: 'resume-builder',
    pageLabel: 'RESUME BUILDER',
    titleTag: 'Resume & Cover Letter Builder | ATS-Friendly CV Maker | DocMania',
    metaDescription:
      'Build ATS-friendly resumes and personalized cover letters online. Professional application package with industry-tailored templates and PDF download.',
    canonicalPath: '/services/resume-builder',
    heroHeadline: 'Create a Recruiter-Ready Resume & Cover Letter in Minutes',
    heroSubtext:
      'Generate a professional resume, create a personalized cover letter, optimize for ATS, and download your complete application package.',
    keywords: ['resume builder', 'cover letter builder', 'CV builder', 'ATS resume', 'professional CV', 'resume maker online'],
    perfectFor: [
      { label: 'Job Seekers', icon: 'users' },
      { label: 'Professionals', icon: 'briefcase' },
      { label: 'Career Switchers', icon: 'refresh' },
      { label: 'Graduates', icon: 'graduate' },
    ],
    ctaLabel: 'Build My Resume',
    ctaPath: '/services/resume-builder',
    documentType: 'resume',
    loginGateMessage: 'Please sign in to build your resume and download PDFs.',
    features: [
      { title: 'ATS-friendly layouts', description: 'Clean structures that parse well in applicant tracking systems.', icon: 'fileCheck' },
      { title: 'Cover letter pairing', description: 'Optionally generate a tailored cover letter from your resume data.', icon: 'file' },
      { title: 'Smart summaries', description: 'Craft compelling professional summaries quickly.', icon: 'wand' },
      { title: 'PDF export', description: 'Download your resume and cover letter as polished PDFs.', icon: 'download' },
    ],
    howItWorks: DEFAULT_HOW_IT_WORKS,
    faqs: [
      { question: 'Are resumes ATS-compatible?', answer: 'Templates use clear headings and standard formatting that work well with most ATS platforms.' },
      { question: 'Can I customize sections?', answer: 'Yes. Add experience, education, skills, and summary sections tailored to your profile.' },
      { question: 'How do I download my resume?', answer: 'Sign in, complete your resume, and export as a professional PDF.' },
    ],
    relatedTools: [
      related('business-proposal-builder', 'Business Proposals'),
      related('invoice-generator', 'Invoice Generator'),
      related('quotation-generator', 'Quotation Generator'),
      related('visa-passport-photo', 'Visa Photo Maker'),
    ],
  },

  'proforma-invoice': {
    slug: 'proforma-invoice',
    pageLabel: 'PROFORMA INVOICE GENERATOR',
    titleTag: 'Proforma Invoice Generator | Pre-Sale Invoices | DocMania',
    metaDescription:
      'Create professional proforma invoices for pre-sale transactions. Branded pre-sale invoice generator with PDF export.',
    canonicalPath: '/services/proforma-invoice',
    heroHeadline: 'Create Professional Proforma Invoices for Pre-Sale Transactions',
    heroSubtext: 'Generate branded pre-sale invoices for clients and procurement approvals.',
    keywords: ['proforma invoice', 'proforma invoice generator', 'pre-sale invoice', 'quotation invoice'],
    perfectFor: [
      { label: 'Suppliers', icon: 'package' },
      { label: 'Exporters', icon: 'globe' },
      { label: 'Procurement', icon: 'cart' },
      { label: 'B2B Transactions', icon: 'handshake' },
    ],
    ctaLabel: 'Create Proforma Invoice',
    ctaPath: '/services/proforma-invoice',
    documentType: 'proforma_invoice',
    loginGateMessage: 'Please sign in to create proforma invoices and download PDFs.',
    features: DEFAULT_FEATURES,
    howItWorks: DEFAULT_HOW_IT_WORKS,
    faqs: [
      { question: 'What is a proforma invoice?', answer: 'A proforma invoice is a preliminary bill sent before goods are delivered, often used for customs, approvals, or pre-payment.' },
      { question: 'Can I brand proforma invoices?', answer: 'Yes. Add your logo and company details to every proforma invoice.' },
      { question: 'Is PDF export available?', answer: 'Yes. Download branded proforma invoices as PDFs for clients and procurement teams.' },
    ],
    relatedTools: [
      related('invoice-generator', 'Invoice Generator'),
      related('quotation-generator', 'Quotation Generator'),
      related('purchase-order-builder', 'Purchase Order'),
      related('delivery-note', 'Delivery Note'),
    ],
  },

  'delivery-note': {
    slug: 'delivery-note',
    pageLabel: 'DELIVERY NOTE GENERATOR',
    titleTag: 'Delivery Note Generator | Goods Dispatch Documents | DocMania',
    metaDescription:
      'Generate professional delivery notes instantly. Delivery note template with sender, receiver, and item tracking details.',
    canonicalPath: '/services/delivery-note',
    heroHeadline: 'Generate Professional Delivery Notes Instantly',
    heroSubtext: 'Create delivery documents with sender, receiver and item tracking details.',
    keywords: ['delivery note template', 'delivery note generator', 'goods delivery note', 'dispatch document'],
    perfectFor: [
      { label: 'Logistics', icon: 'truck' },
      { label: 'Vendors', icon: 'cart' },
      { label: 'Retailers', icon: 'store' },
      { label: 'Warehouses', icon: 'package' },
    ],
    ctaLabel: 'Create Delivery Note',
    ctaPath: '/services/delivery-note',
    documentType: 'delivery_note',
    loginGateMessage: 'Please sign in to create delivery notes and download PDFs.',
    features: DEFAULT_FEATURES,
    howItWorks: DEFAULT_HOW_IT_WORKS,
    faqs: [
      { question: 'What details can I include?', answer: 'Add sender and receiver information, item lists, quantities, and delivery confirmation fields.' },
      { question: 'Is this suitable for proof of delivery?', answer: 'Yes. Delivery notes serve as formal records of goods dispatched and received.' },
      { question: 'Can I print delivery notes?', answer: 'Export PDFs optimized for printing and signing on delivery.' },
    ],
    relatedTools: [
      related('waybill-generator', 'Waybill Generator'),
      related('purchase-order-builder', 'Purchase Order'),
      related('invoice-generator', 'Invoice Generator'),
      related('proforma-invoice', 'Proforma Invoice'),
    ],
  },

  'waybill-generator': {
    slug: 'waybill-generator',
    pageLabel: 'WAYBILL GENERATOR',
    titleTag: 'Waybill Generator | Shipping & Courier Documents | DocMania',
    metaDescription:
      'Create professional waybills for shipping and delivery. Courier waybill generator with sender, receiver, and parcel details.',
    canonicalPath: '/services/waybill-generator',
    heroHeadline: 'Create Professional Waybills for Shipping & Delivery',
    heroSubtext: 'Generate shipment-ready waybills with sender, receiver and parcel details.',
    keywords: ['waybill template', 'shipping waybill', 'courier waybill', 'logistics waybill'],
    perfectFor: [
      { label: 'Logistics Companies', icon: 'truck' },
      { label: 'Dispatch Riders', icon: 'bike' },
      { label: 'Courier Services', icon: 'package' },
      { label: 'E-commerce', icon: 'bag' },
    ],
    ctaLabel: 'Create Waybill',
    ctaPath: '/services/waybill-generator',
    documentType: 'waybill',
    loginGateMessage: 'Please sign in to create waybills and download PDFs.',
    features: DEFAULT_FEATURES,
    howItWorks: DEFAULT_HOW_IT_WORKS,
    faqs: [
      { question: 'What is a waybill used for?', answer: 'Waybills track shipments between sender and receiver, documenting cargo details and delivery routes.' },
      { question: 'Can couriers use these waybills?', answer: 'Yes. Templates include fields for parcel details, origins, destinations, and contact information.' },
      { question: 'Is branding supported?', answer: 'Add your company logo and details for professional logistics documentation.' },
    ],
    relatedTools: [
      related('delivery-note', 'Delivery Note'),
      related('purchase-order-builder', 'Purchase Order'),
      related('invoice-generator', 'Invoice Generator'),
      related('pdf-compression', 'PDF Compression'),
    ],
  },

  'pdf-compression': {
    slug: 'pdf-compression',
    pageLabel: 'PDF COMPRESSOR',
    titleTag: 'Compress PDF Files Online | Reduce PDF Size | DocMania',
    metaDescription:
      'Compress PDF files online without losing quality. Reduce PDF size for visa uploads, email attachments, and business submissions.',
    canonicalPath: '/services/pdf-compression',
    heroHeadline: 'Compress PDF Files Online Without Losing Quality',
    heroSubtext: 'Reduce PDF size for visa uploads (Target Size), email attachments and business submissions.',
    keywords: ['compress pdf', 'pdf compressor', 'reduce pdf size', 'compress pdf online', 'visa upload pdf', 'reduce file size'],
    perfectFor: [
      { label: 'Visa Uploads', icon: 'globe' },
      { label: 'Loan Applications', icon: 'landmark' },
      { label: 'Email Attachments', icon: 'mail' },
      { label: 'Business Documents', icon: 'file' },
    ],
    ctaLabel: 'Compress PDF',
    ctaPath: '/services/pdf-compression',
    isUtility: true,
    loginGateMessage: 'Please sign in to upload and compress PDF files.',
    features: [
      { title: 'Standard & aggressive modes', description: 'Choose balanced compression or maximum size reduction.', icon: 'zap' },
      { title: 'Target file size', description: 'Compress to under 2 MB, 5 MB, 10 MB, or a custom target.', icon: 'check' },
      { title: 'Visa-ready output', description: 'Meet embassy and portal upload limits without manual resizing.', icon: 'shield' },
      { title: 'Instant download', description: 'Get your compressed PDF immediately after processing.', icon: 'download' },
    ],
    howItWorks: [
      { step: 1, title: 'Upload your PDF', description: 'Select a PDF up to 50 MB and choose your compression mode.' },
      { step: 2, title: 'Compress online', description: 'DocMania reduces file size while preserving document readability.' },
      { step: 3, title: 'Download result', description: 'Save the smaller PDF for visa portals, email, or submissions.' },
    ],
    faqs: [
      { question: 'Will compression reduce document quality?', answer: 'Standard mode balances size and clarity. Aggressive mode maximizes reduction with lower image quality.' },
      { question: 'Can I compress to under 2 MB for visa uploads?', answer: 'Yes. Use target size mode with the 2 MB preset designed for visa and embassy portals.' },
      { question: 'What is the maximum file size?', answer: 'You can upload PDFs up to 50 MB for compression.' },
      { question: 'Do I need to sign in?', answer: 'You can view the tool publicly. Sign in is required to upload, compress, and download files.' },
    ],
    relatedTools: [
      related('visa-passport-photo', 'Visa Photo Maker'),
      related('invoice-generator', 'Invoice Generator'),
      related('business-proposal-builder', 'Business Proposals'),
      related('resume-builder', 'Resume Builder'),
    ],
  },

  'visa-passport-photo': {
    slug: 'visa-passport-photo',
    pageLabel: 'VISA & PASSPORT PHOTO MAKER',
    titleTag: 'Visa & Passport Photo Maker Online | DocMania',
    metaDescription:
      'Create visa and passport photos online in minutes. Embassy-compliant passport photo maker with proper sizing and instant download.',
    canonicalPath: '/services/visa-passport-photo',
    heroHeadline: 'Create Visa & Passport Photos Online in Minutes',
    heroSubtext: 'Generate embassy-compliant passport and visa photos with proper sizing.',
    keywords: ['passport photo maker', 'visa photo online', 'passport photo online', 'visa photo generator', 'ID photo maker'],
    perfectFor: [
      { label: 'Visa Applications', icon: 'globe' },
      { label: 'Passport Renewals', icon: 'fileCheck' },
      { label: 'Embassy Submissions', icon: 'landmark' },
      { label: 'ID Cards', icon: 'camera' },
    ],
    ctaLabel: 'Create Visa Photo',
    ctaPath: '/services/visa-passport-photo',
    isUtility: true,
    loginGateMessage: 'Please sign in to generate and download visa photos.',
    features: [
      { title: 'Country presets', description: 'Select embassy-compliant dimensions for popular visa and passport requirements.', icon: 'globe' },
      { title: 'Digital & print formats', description: 'Get web-ready JPEGs or 4R print sheets for photo labs.', icon: 'camera' },
      { title: 'Background processing', description: 'Automatic cropping and sizing to official specifications.', icon: 'wand' },
      { title: 'Fast turnaround', description: 'Upload a portrait and receive compliant photos in minutes.', icon: 'zap' },
    ],
    howItWorks: [
      { step: 1, title: 'Upload a portrait', description: 'Use a clear front-facing photo with good lighting.' },
      { step: 2, title: 'Select country preset', description: 'Choose the visa or passport size requirement for your application.' },
      { step: 3, title: 'Download compliant photo', description: 'Get a properly sized digital or print-ready image.' },
    ],
    faqs: [
      { question: 'Are photos embassy-compliant?', answer: 'Presets follow common official dimensions. Always verify requirements for your specific application.' },
      { question: 'Can I get a print sheet?', answer: 'Yes. Choose print format to download a 4R sheet suitable for photo labs.' },
      { question: 'What file types are accepted?', answer: 'Upload standard image formats such as JPEG or PNG up to 20 MB.' },
      { question: 'Do I need to sign in?', answer: 'You can explore presets publicly. Sign in is required to generate and download photos.' },
    ],
    relatedTools: [
      related('pdf-compression', 'PDF Compression'),
      related('resume-builder', 'Resume Builder'),
      related('invoice-generator', 'Invoice Generator'),
      related('business-proposal-builder', 'Business Proposals'),
    ],
  },
};

export function getToolLandingConfig(slug) {
  return TOOL_LANDING_CONFIGS[slug] || null;
}

export function resolveIcon(name) {
  return ICON[name] || Sparkles;
}
